const snack = require("../../../../../components/snack/snack");
const { getReportDefinition } = require("./reportHub-report-registry");
const customerReports = require("./reportHub-report-customer");
const providerReports = require("./reportHub-report-provider");
const purchaseReports = require("./reportHub-report-purchase");
const saleReports = require("./reportHub-report-sale");
const inventoryReports = require("./reportHub-report-inventory");
const transactionReports = require("./reportHub-report-transaction");

let activeDefinition = null;
let outputMode = "pdf"; // pdf | sheet

const AUTOCOMPLETE_IDS = [
  "reportHub_form_customer_id",
  "reportHub_form_provider_id",
  "reportHub_form_purchase_id",
  "reportHub_form_sale_id",
  "reportHub_form_transaction_id",
];

// --- Helpers de DOM ---
const getEl = (id) => document.getElementById(id);
const getElValue = (id) => getEl(id)?.value || "";

const toggleVisibility = (el, show) => el?.classList.toggle("dn", !show);

function clearReportOutput() {
  const out = getEl("reportHub-report-output");
  if (out) out.innerHTML = "";
}

function getSelectedEntityDbid(elementId) {
  const el = getEl(elementId);
  if (!el) return "";
  try {
    const sel = JSON.parse(el.getAttribute("selected") || "[]");
    return sel.length ? String(sel[0]) : "";
  } catch (e) {
    return "";
  }
}

function clearAutocomplete(elementId) {
  const el = getEl(elementId);
  if (!el) return;

  el.setAttribute("selected", "[]");
  const chipCont = el.querySelector(".chip-cont");
  if (chipCont) chipCont.innerHTML = "";
  const input = el.querySelector(".elem");
  if (input) input.value = "";
}

function clearAllAutocompletes() {
  AUTOCOMPLETE_IDS.forEach(clearAutocomplete);
}

const getSelectedCustomerDbid = () =>
  getSelectedEntityDbid("reportHub_form_customer_id");
const getSelectedProviderDbid = () =>
  getSelectedEntityDbid("reportHub_form_provider_id");

function getAutocompleteEntityId(def) {
  const map = {
    customer: () => getSelectedCustomerDbid(),
    provider: () => getSelectedProviderDbid(),
    purchase: () => getSelectedEntityDbid("reportHub_form_purchase_id"),
    sale: () => getSelectedEntityDbid("reportHub_form_sale_id"),
    transaction: () =>
      getSelectedEntityDbid("reportHub_form_transaction_id"),
  };
  const fn = map[def.autocompleteEntity];
  return fn ? fn() : "";
}

function applyAutocompleteVisibility(def) {
  const wraps = {
    customer: getEl("reportHub-filter-customer-wrap"),
    provider: getEl("reportHub-filter-provider-wrap"),
    purchase: getEl("reportHub-filter-purchase-wrap"),
    sale: getEl("reportHub-filter-sale-wrap"),
    transaction: getEl("reportHub-filter-transaction-wrap"),
  };

  Object.keys(wraps).forEach((k) => {
    toggleVisibility(wraps[k], false);
  });

  const entityWrap = getEl("reportHub-filter-entity-wrap");
  toggleVisibility(entityWrap, def.needsAutocomplete);

  if (def.needsAutocomplete && def.autocompleteEntity) {
    toggleVisibility(wraps[def.autocompleteEntity], true);
  }
}

function applyFilterVisibility(def) {
  toggleVisibility(getEl("reportHub-filter-dates"), def.filterDateRange);
  toggleVisibility(getEl("reportHub-filter-year-wrap"), def.filterYear);

  applyAutocompleteVisibility(def);

  const toggleRow = getEl("reportHub-output-toggle-row");
  toggleVisibility(toggleRow, def.allowSheet !== false);
}

function syncRadiosFromOutputMode() {
  const pdf = getEl("reportHub_radio_pdf");
  const sheet = getEl("reportHub_radio_sheet");
  if (!pdf || !sheet) return;
  pdf.checked = outputMode === "pdf";
  sheet.checked = outputMode === "sheet";
}

function bindOutputTypeRadios() {
  const wrap = getEl("reportHub-output-toggle-row");
  if (!wrap || wrap.getAttribute("data-hub-radio-bound") === "1") return;
  wrap.setAttribute("data-hub-radio-bound", "1");
  const pdf = getEl("reportHub_radio_pdf");
  const sheet = getEl("reportHub_radio_sheet");
  if (!pdf || !sheet) return;
  const syncModeFromRadios = () => {
    outputMode = sheet.checked ? "sheet" : "pdf";
  };
  pdf.addEventListener("change", syncModeFromRadios);
  sheet.addEventListener("change", syncModeFromRadios);
}

export function setOutputMode(mode) {
  outputMode = mode === "sheet" ? "sheet" : "pdf";
  syncRadiosFromOutputMode();
}

export function initFilters() {
  populateYearSelect();
  bindOutputTypeRadios();
  syncRadiosFromOutputMode();
}

function populateYearSelect() {
  const sel = getEl("reportHub_filter_year_select");
  if (!sel || sel.getAttribute("data-hub-years") === "1") return;
  sel.setAttribute("data-hub-years", "1");
  const y = new Date().getFullYear();
  sel.innerHTML =
    `<option value="" disabled selected>Select year</option>` +
    Array.from({ length: 12 }, (_, i) => {
      const yr = y - i;
      return `<option value="${yr}">${yr}</option>`;
    }).join("");
}

function clearFilterInputs() {
  const from = getEl("reportHub_filter_date_from");
  const to = getEl("reportHub_filter_date_to");
  const yearSel = getEl("reportHub_filter_year_select");

  if (from) from.value = "";
  if (to) to.value = "";
  if (yearSel) {
    yearSel.selectedIndex = 0;
  }

  const pdfRadio = getEl("reportHub_radio_pdf");
  const sheetRadio = getEl("reportHub_radio_sheet");
  if (pdfRadio) pdfRadio.checked = true;
  if (sheetRadio) sheetRadio.checked = false;

  clearAllAutocompletes();
}

function updateGeneratingTitle(def) {
  const title = getEl("reportHub-generating-title");
  if (title && def && def.label) {
    title.textContent = "Generating — " + def.label;
  }
}

export function onCleanAll() {
  clearFilterInputs();
  clearReportOutput();
  outputMode = "pdf";
  syncRadiosFromOutputMode();
}

export function executeReport(def, filters) {
  clearReportOutput();

  const handlers = {
    customer: () =>
      customerReports.getData(def, filters, { getSelectedCustomerDbid }),
    provider: () =>
      providerReports.getData(def, filters, { getSelectedProviderDbid }),
    purchase: () => purchaseReports.getData(def, filters),
    sale: () => saleReports.getData(def, filters),
    inventory: () => inventoryReports.getData(def, filters),
    transaction: () => transactionReports.getData(def, filters),
  };

  const handler = handlers[def.handler];
  handler ? handler() : snack.start({ act: "show", id: "downloadError" });
}

export function onSelectReport(detail) {
  const def = getReportDefinition(detail.reportId);
  if (!def) return;

  activeDefinition = def;
  outputMode = "pdf";
  clearFilterInputs();
  applyFilterVisibility(def);
  syncRadiosFromOutputMode();
  updateGeneratingTitle(def);

  const filterCard = getEl("reportHub-filter-card");

  if (def.autoPrintWithoutUI && !def.needsAutocomplete) {
    toggleVisibility(filterCard, false);
    executeReport(def, buildFiltersFromForm(true));
    return;
  }

  toggleVisibility(filterCard, true);
}

function buildFiltersFromForm(skipSheetCheck) {
  let startDate = getElValue("reportHub_filter_date_from");
  let endDate = getElValue("reportHub_filter_date_to");
  const yearSel = getEl("reportHub_filter_year_select");
  const reportYear =
    yearSel && yearSel.value && !Number.isNaN(Number(yearSel.value))
      ? yearSel.value
      : "";

  if (
    activeDefinition &&
    activeDefinition.filterYear &&
    reportYear &&
    !startDate &&
    !endDate
  ) {
    startDate = `${reportYear}-01-01`;
    endDate = `${reportYear}-12-31`;
  }

  const filters = {
    startDate,
    endDate,
    useYear: !!reportYear,
    reportYear,
    outputMode,
    entityId:
      activeDefinition && activeDefinition.needsAutocomplete
        ? getAutocompleteEntityId(activeDefinition)
        : "",
  };

  if (
    !skipSheetCheck &&
    outputMode === "sheet" &&
    activeDefinition &&
    activeDefinition.allowSheet
  ) {
    snack.start({ act: "show", id: "reportHubSheetSoon" });
    return null;
  }

  return filters;
}

export function onRunFilteredReport() {
  if (!activeDefinition) return;

  const filters = buildFiltersFromForm(false);
  if (!filters) return;

  executeReport(activeDefinition, filters);
}
